import type { NutritionEU } from "api-types";

import React from "react";

import { Box, Typography, styled, useTheme } from "@mui/material";

import { useTranslation } from "../../../i18n";
import {
  NUTRIENT_ROWS,
  formatEnergy,
  formatGrams,
  formatPortionSize,
  nutritionTableColumns,
} from "./dish-nutrition-format";

/** The dish's nutrition table, per 100 g (or ml) and per portion. */
export const DishNutrition = ({ nutrition }: { nutrition: NutritionEU }) => {
  const { t, i18n } = useTranslation();
  const { oscar } = useTheme().palette;
  const { language } = i18n;

  const unit = nutrition.unit === "ml" ? "ml" : "g";
  const columns = nutritionTableColumns(nutrition);

  const amount = (value: number | null, text: string) => (
    <Typography
      variant="bodyRRegular"
      color={
        value === null
          ? oscar.light.content.content2
          : oscar.light.content.content1
      }
    >
      {text}
    </Typography>
  );

  const note = (text: string) => (
    <Typography
      display="block"
      variant="bodySLight"
      color={oscar.light.content.content2Text}
    >
      {text}
    </Typography>
  );

  return (
    <Box width="100%">
      <NutritionTable>
        <thead>
          <tr>
            <td />
            {columns.map(({ key }) => (
              <th key={key} scope="col">
                <Typography
                  variant="labelRMedium"
                  color={oscar.light.content.content1}
                >
                  {key === "per100"
                    ? t("dish.nutrition.per100", { unit })
                    : t("dish.nutrition.perPortion")}
                </Typography>
                {key === "perPortion" && nutrition.portionSize !== null
                  ? note(
                      formatPortionSize(nutrition.portionSize, unit, language)
                    )
                  : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">
              <Typography
                variant="bodyRLight"
                color={oscar.light.content.content1}
              >
                {t("dish.nutrition.energy")}
              </Typography>
            </th>
            {columns.map(({ key, values }) => (
              <td key={key}>
                {amount(
                  values.energyKcal,
                  formatEnergy(values.energyKcal, "kcal", language)
                )}
                {values.energyKj !== null
                  ? note(formatEnergy(values.energyKj, "kJ", language))
                  : null}
              </td>
            ))}
          </tr>
          {NUTRIENT_ROWS.map(({ nutrient, ofWhich }) => (
            <tr key={nutrient}>
              <th scope="row">
                <Typography
                  variant="bodyRLight"
                  color={
                    ofWhich
                      ? oscar.light.content.content2
                      : oscar.light.content.content1
                  }
                  paddingLeft={ofWhich ? 1.25 : undefined}
                >
                  {t(`dish.nutrition.${nutrient}`)}
                </Typography>
              </th>
              {columns.map(({ key, values }) => (
                <td key={key}>
                  {amount(
                    values[nutrient],
                    formatGrams(nutrient, values[nutrient], language)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </NutritionTable>
      <Typography
        component="p"
        variant="bodySLight"
        color={oscar.light.content.content2Text}
        textAlign="center"
        marginTop={1.5}
      >
        {t("dish.nutrition.footnote")}
      </Typography>
    </Box>
  );
};

const NutritionTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  fontVariantNumeric: "tabular-nums",
  "& th, & td": {
    padding: theme.spacing(1, 0.75),
    textAlign: "right",
    verticalAlign: "top",
    borderBottom: `1px solid ${theme.palette.oscar.light.borders.gray}`,
  },
  "& thead th": {
    padding: theme.spacing(0.5, 0.75),
    verticalAlign: "middle",
  },
  "& tbody th": {
    textAlign: "left",
  },
  "& tbody tr:last-of-type > *": {
    borderBottom: "none",
  },
}));
